import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Position } from '../../types/user';
import { getPositions } from '../../services/userApi';

interface CreateUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (userData: {
        name: string;
        email: string;
        password: string;
        password_confirmation: string;
        utype: 'ADMIN' | 'USER';
        position_id?: number;
    }) => void;
}

export default function CreateUserModal({ isOpen, onClose, onSubmit }: CreateUserModalProps) {
    const [positions, setPositions] = useState<Position[]>([]);
    const [isLoadingPositions, setIsLoadingPositions] = useState(false);
    const [formData, setFormData] = useState<{
        name: string;
        email: string;
        password: string;
        password_confirmation: string;
        utype: 'ADMIN' | 'USER';
        position_id?: number;
    }>({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        utype: 'USER',
        position_id: undefined
    });

    useEffect(() => {
        if (isOpen) {
            fetchPositions();
        }
    }, [isOpen]);

    const fetchPositions = async () => {
        try {
            setIsLoadingPositions(true);
            const response = await getPositions();
            setPositions(response.data);
        } catch (error) {
            console.error('Failed to fetch positions:', error);
        } finally {
            setIsLoadingPositions(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Create New User</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input
                                type="text"
                                required
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                required
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Password</label>
                            <input
                                type="password"
                                required
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                            <input
                                type="password"
                                required
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                value={formData.password_confirmation}
                                onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Role</label>
                            <select
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                value={formData.utype}
                                onChange={(e) => setFormData({ ...formData, utype: e.target.value as 'ADMIN' | 'USER' })}
                            >
                                <option value="USER">User</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Position</label>
                            <select
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                value={formData.position_id || ''}
                                onChange={(e) => setFormData({ 
                                    ...formData, 
                                    position_id: e.target.value ? Number(e.target.value) : undefined 
                                })}
                            >
                                <option value="">Select a position</option>
                                {positions.map((position) => (
                                    <option key={position.id} value={position.id}>
                                        {position.name} ({position.department?.name})
                                    </option>
                                ))}
                            </select>
                            {isLoadingPositions && <span className="text-sm text-gray-500">Loading positions...</span>}
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Create User
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 